import React from "react";
import { CHANGE_LABELS, ChangeKind } from "../lib/changes";
import { Property } from "../types";
import WatchlistToggle from "./WatchlistToggle";
import { statusMeta } from "../lib/statusMeta";
import { isVacantLand } from "../lib/propertyUrls";
import {
  dataCompleteness,
  resolveDisplayAddress,
  resolveDisplayParcelId,
  resolveAmountOwed,
  resolveDisplayStatus,
  resolvePropertyBadge,
  hasUpsetWindowEvidence,
} from "../lib/dataQuality";
import {
  formatAddedDate,
  formatCurrency as sharedFormatCurrency,
  formatShortDate as sharedFormatShortDate,
} from "../lib/format";
import DealScore from "./DealScore";
import { codeViolationEvidence } from "../lib/codeViolationEvidence";
import OtherSignalPills from "./OtherSignalPills";

interface PropertyCardProps {
  property: Property;
  isSelected: boolean;
  isHighlighted: boolean; // in current filter/search results
  onSelect: (property: Property | null) => void;
  authed: boolean;
  isWatched: boolean;
  watchLoading?: boolean;
  // isAllowedCounty — geography-aware tier gate (Free/Scout redesign). True
  // for Pro/team/admin, or when this property's county is one of the
  // viewer's chosen counties. When false the DealScore/status pills/
  // watchlist toggle and the 3-field data strip render as a fuzzed
  // placeholder instead of real values (the backend already zeroes
  // assessedValue/estMarketValue server-side for these rows — this prop
  // just drives the honest "why" messaging instead of a bare "—").
  isAllowedCounty: boolean;
  watchLimit: number; // current saved count
  watchLimitMax: number; // max allowed for plan
  onToggleWatch: (propertyId: string) => void;
  // "Not interested" (#3074): omit to render no hide control.
  isHidden?: boolean;
  onToggleHidden?: (propertyId: string) => void;
  // Deal stage (#3075), shown as a pill on the watchlist tab.
  stageLabel?: string;
  // What changed since the user's last visit (#3207): the strongest kind, if any.
  changeKind?: ChangeKind;
  // List <-> map hover sync (#3077): highlighted while its pin is hovered,
  // and reports the pointer entering/leaving the card.
  isHovered?: boolean;
  onHoverChange?: (id: string | null) => void;
  // Bulk select (#3079): a checkbox renders only while onToggleChecked is set.
  isChecked?: boolean;
  onToggleChecked?: (id: string) => void;
  onRequireAuth: () => void;
  onRequirePro: () => void;
  // Set only for a free/scout viewer who has not picked a county yet. The
  // fuzzed "outside your area" CTA then opens the county picker (their free
  // county is the fix) instead of the paid upgrade prompt.
  onPickCounty?: () => void;
  // activeLeadTypeFilter — the lead-type filter currently applied to this
  // list (e.g. "mortgage_foreclosure"), threaded down so the headline badge
  // can lead with the signal that actually matched the filter instead of the
  // fixed-priority default. See resolvePropertyBadge. Optional — omitting it
  // keeps the original priority-ranked behavior.
  activeLeadTypeFilter?: string | string[];
}

// Status colors AND the 4-stage lifecycle (NOD → Notice Filed → Auction →
// Resolved) both come from the shared registry (lib/statusMeta.ts) —
// statusMeta(status).lifecycleStage below. This used to be a local
// statusToLifecycleStage() switch duplicating STATUS_META.lifecycleStage;
// removed since the two were required to stay in sync by hand (#987).

// NC statutory min between NOD filing and auction (rough) — used as a
// pre-foreclosure footer fallback when no filing date is on file.
const NC_NOD_TO_AUCTION_DAYS = 45;

function estimateDaysToAuction(property: Property): number | null {
  if (property.status !== "notice_of_default") return null;
  const filing = property.sale?.nodFiledDate;
  if (filing) {
    const filedMs = new Date(filing).getTime();
    if (!isNaN(filedMs)) {
      const elapsed = Math.floor((Date.now() - filedMs) / (1000 * 60 * 60 * 24));
      const remaining = NC_NOD_TO_AUCTION_DAYS - elapsed;
      return remaining > 0 ? remaining : NC_NOD_TO_AUCTION_DAYS;
    }
  }
  return NC_NOD_TO_AUCTION_DAYS;
}

// Full-dollar formatter ("$245,000"); an em dash when genuinely unknown
// (never a fabricated "$0"). NC upset rule: a new bid must raise the current
// by 5% or $750, whichever is greater — that's the "min next bid".
// Delegates to lib/format.ts (#1033); `treatZeroAsEmpty` preserves the
// original `<= 0` guard (no negative money field exists in this app, so
// the two checks are equivalent in practice).
function formatCurrencyFull(value: number | null | undefined): string {
  return sharedFormatCurrency(value, { fallback: "—", treatZeroAsEmpty: true });
}

// Delegates to lib/format.ts (#1033). `padDay: true` preserves the
// original "Jan 05, 2026" rendering (zero-padded day) — BidTimeline's
// equivalent formatter does NOT pad ("Jan 5, 2026"); both are kept
// distinct rather than silently normalized to one convention.
function formatShortDate(iso: string): string {
  return sharedFormatShortDate(iso, { padDay: true });
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  isSelected,
  isHighlighted,
  onSelect,
  authed,
  isWatched,
  watchLoading = false,
  isAllowedCounty,
  watchLimit,
  watchLimitMax,
  onToggleWatch,
  isHidden = false,
  onToggleHidden,
  stageLabel,
  changeKind,
  isHovered = false,
  onHoverChange,
  isChecked = false,
  onToggleChecked,
  onRequireAuth,
  onRequirePro,
  onPickCounty,
  activeLeadTypeFilter,
}) => {
  // atLimit — watchLimitMax already encodes the correct per-plan cap
  // (free=3, scout=10, pro/team/admin=effectively unlimited; see
  // lib/planCapabilities.ts), so comparing against it directly is
  // correct for every tier without a separate isPro shortcut. Before
  // #677 this read `!isPro && !isWatched && watchLimit >= watchLimitMax`
  // — the `!isPro` short-circuit made this always false for Scout
  // (isPro=true), so a Scout user's "at limit" star never disabled at
  // their real cap of 10; only the backend's 403 (see useWatchlist.ts)
  // caught it, after the fact. Not a data/access leak (the backend cap
  // held throughout), just an inconsistent client-side UX for Scout.
  const atLimit = !isWatched && watchLimit >= watchLimitMax;
  // Display-status guard: a stale auction_scheduled row whose auction date is
  // already in the past renders as auction_passed (honest "date passed, outcome
  // pending") until the backend transitioner advances it. See resolveDisplayStatus.
  const displayStatus = resolveDisplayStatus(property);
  const meta = statusMeta(displayStatus);
  // Headline pill: the lifecycle STATUS for a live auction, or the highest-
  // priority LEAD TYPE ("Tax Foreclosure", "Probate", ...) for a pre-auction
  // lead — see resolvePropertyBadge for the full rule.
  const badge = resolvePropertyBadge(property, activeLeadTypeFilter);
  const cvEvidence = codeViolationEvidence(property);
  const lifecycleStage = meta.lifecycleStage;
  // A held sale (stage 3) shows the final bid and spread; only a terminal
  // status is styled as closed. pending_confirmation is both: held, but live.
  const saleHeld = lifecycleStage === 3;
  const isTerminal = meta.isTerminal;
  const isPreForeclosure = property.status === "notice_of_default";

  const completeness = dataCompleteness(property);
  // Vacant-land signal (#1604) — reuses the SAME isVacantLand() heuristic
  // already shipped and tested for the Zillow-link builder (propertyUrls.ts),
  // rather than property.propertyClass. Checked live 2026-08-19:
  // propertyClass="land_only" is populated for ~66% of Mecklenburg and ~5%
  // of Guilford but is essentially 0 everywhere else (most counties never run
  // a scraper path that sets it) — it is not a usable signal outside those
  // two counties. isVacantLand reads the typed `building` group
  // (structureCount/totalSqft/landUseDesc), which is populated far more
  // broadly.
  const vacant = isVacantLand(property);
  const displayAddr = resolveDisplayAddress(property);
  const owed = resolveAmountOwed(property);
  const equity =
    property.assessedValue && property.amountDue ? property.assessedValue - property.amountDue : null;

  // Server-parsed (#1872): these arrive as numbers, so the local
  // currency-string parser this file used to carry is gone.
  const openingNum = property.sale?.openingBid ?? null;
  const currentNum = property.sale?.currentBid ?? null;
  // A recorded bid alone doesn't mean the upset window is open: a pre-sale listing
  // can carry a bid figure too (Guilford's layer does, for a sale weeks away).
  // Only claim an open window, and price the next upset bid off that figure, when
  // the dates can back it. See hasUpsetWindowEvidence.
  const upsetWindowOpen = displayStatus === "auction_scheduled" && currentNum != null && hasUpsetWindowEvidence(property);

  // ── Meta line: "PIN <parcel> · City ZIP · County" (synthetic CASE: ids hidden).
  const countyName = (property.countyId || "")
    .replace(/-nc$/i, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const cityPart = displayAddr.city ? [displayAddr.city, displayAddr.zip].filter(Boolean).join(" ") : "";
  const resolvedParcelId = resolveDisplayParcelId(property);
  const pinStr = resolvedParcelId.value ? `PIN ${resolvedParcelId.value}` : "";
  const metaLine = [pinStr, cityPart, countyName].filter(Boolean).join(" · ");

  // ── Property facts + owner (quick-add ahead of the full card redesign, #2254).
  //    Facts (beds/baths/sqft/year) come from the FREE `building` view — render
  //    only what a county actually publishes, never a fabricated "0". The owner
  //    block is PII and is Pro-gated AT THE SOURCE: the backend populates
  //    property.owner only for entitled (Pro + in-plan) viewers, so a free user
  //    never receives a name to render here. Mailing address stays off the card
  //    by design — it's the paid outreach payload (see the detail view / Leads).
  const building = property.building;
  const factParts: string[] = [];
  if (building?.bedrooms != null && building.bedrooms > 0) factParts.push(`${Math.round(building.bedrooms)} bd`);
  const baths = (building?.fullBaths ?? 0) + (building?.halfBaths ?? 0) * 0.5;
  if (baths > 0) factParts.push(`${baths % 1 === 0 ? baths : baths.toFixed(1)} ba`);
  if (building?.totalSqft != null && building.totalSqft > 0)
    factParts.push(`${Math.round(building.totalSqft).toLocaleString("en-US")} sqft`);
  if (building?.yearBuilt != null && building.yearBuilt > 0) factParts.push(`Built ${Math.round(building.yearBuilt)}`);
  const factsLine = factParts.join("  ·  ");
  const ownerName = property.owner?.name?.trim() || "";
  // #2256: a probate/estate lead's owner-of-record row is the deceased's
  // estate, not a living titleholder — labeling it "Owner" reads as though
  // the card is naming the person selling the property. The county record
  // itself (property.owner.name, e.g. "ESTATE OF JOHN SMITH") is unchanged;
  // only the LABEL differs. Checked against the full leadSignals array, not
  // just the headline badge — a probate signal riding alongside a stronger
  // headline signal (e.g. tax_foreclosure) still means the owner-of-record
  // is the estate, so the label must reflect that regardless of which
  // signal won the badge priority race (see leadTypeMeta.LEAD_SIGNAL_PRIORITY).
  const isProbateLead = (property.leadSignals ?? []).some((s) => s.signalType === "probate");
  const ownerRowLabel = isProbateLead ? "Estate representative" : "Owner";
  const ownerRowTitle = isProbateLead
    ? "Estate representative (from probate filing) — not a living titleholder"
    : "Owner of record (from county records)";

  // ── Status-specific middle metric + equity/spread (matches the List Row design).
  let midLabel: string;
  let midVal: number | null;
  let equityLabel = "Est. equity";
  let equityVal: number | null = equity;
  if (saleHeld) {
    const finalBid = currentNum ?? openingNum;
    midLabel = "Final bid";
    midVal = finalBid;
    equityLabel = "Spread";
    equityVal = property.assessedValue > 0 && finalBid != null ? property.assessedValue - finalBid : null;
  } else if (displayStatus === "auction_scheduled") {
    if (upsetWindowOpen && currentNum != null) {
      midLabel = "Min next bid";
      midVal = currentNum + Math.max(currentNum * 0.05, 750);
    } else {
      // Before a sale the only bid that can exist is the opening one, so a bid
      // figure on a not-yet-held auction is shown as such, not as a live bid.
      midLabel = "Opening bid";
      midVal = openingNum ?? currentNum;
    }
  } else if (property.status === "notice_filed") {
    midLabel = "Taxes owed";
    midVal = owed.amount;
  } else if (isPreForeclosure) {
    midLabel = "Est. owed";
    midVal = owed.amount;
  } else {
    midLabel = "Owed";
    midVal = owed.amount;
  }

  // ── Footer line (status-specific). Amber clock only when an upset window is
  //    genuinely open (an auction row with a recorded current bid AND dates that
  //    back an open window). No fabricated HH:MM upset timer — we don't store an
  //    upset deadline yet (follow-up).
  let footerMain: string;
  let footerRight = "";
  let footerAmber = false;
  const saleDate = property.auctionDate ? formatShortDate(property.auctionDate) : "";
  const saleTime = property.sale?.saleTime || "";
  const saleLoc = property.sale?.saleLocation || "";
  if (upsetWindowOpen) {
    footerAmber = true;
    footerMain = "Upset window open";
    footerRight = saleDate ? `Sale ${saleDate}` : "";
  } else if (displayStatus === "auction_scheduled") {
    footerMain = saleDate
      ? ["Sale " + saleDate, saleTime, saleLoc].filter(Boolean).join(" · ")
      : "Sale date pending";
    // The firm's feed stopped listing this sale (cancelled, paused or resolved
    // are all possible). Say so instead of presenting the date as live.
    if (saleDate && property.sale?.listingStale) {
      footerMain = `Sale ${saleDate} · unconfirmed, firm dropped it`;
    }
  } else if (property.status === "notice_filed") {
    footerMain = "Notice filed · no sale date yet";
  } else if (isPreForeclosure) {
    const filed = property.sale?.nodFiledDate;
    const eta = estimateDaysToAuction(property);
    footerMain = filed
      ? `Default notice ${formatShortDate(filed)}`
      : eta
      ? `Pre-foreclosure · ~${eta}d to auction (est.)`
      : "Pre-foreclosure · no sale scheduled";
  } else if (saleHeld) {
    footerMain = saleDate ? `${meta.short} ${saleDate} · upset window closed` : `${meta.short} · upset window closed`;
  } else {
    footerMain = meta.desc;
  }

  // First-ingest date (createdAt), not the filing date — labelled "Added" so
  // it is not read as one. Empty for locked/fixture rows with no timestamp.
  const addedDate = formatAddedDate(property.createdAt, { padDay: true });

  const limitedDataTitle = [
    completeness.sourceLimitedNote
      ? completeness.sourceLimitedNote
      : completeness.missing.length
      ? `Missing: ${completeness.missing.join(", ")}`
      : "",
    ...completeness.conflicts,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleClick = () => onSelect(isSelected ? null : property);

  // Reuse the SAME UpgradePrompt trigger onRequirePro already wires up for
  // the watchlist toggle — don't build a second modal-trigger path. Stop
  // propagation so clicking the CTA doesn't also open the property (the
  // card's onClick is on the outer div).
  const handleFuzzedUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onPickCounty) onPickCounty();
    else onRequirePro();
  };

  const cardClass = [
    "property-card",
    `property-card--${property.status || "unknown"}`,
    isSelected ? "property-card--selected" : "",
    isHovered && !isSelected ? "property-card--hovered" : "",
    !isHighlighted ? "property-card--dimmed" : "",
    isTerminal ? "property-card--terminal" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClass}
      data-tour="property-card"
      onClick={handleClick}
      onMouseEnter={onHoverChange ? () => onHoverChange(property.id) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(null) : undefined}
      // Keyboard parity for the click-to-select card (WCAG 2.1.1). Only fire
      // when the card itself has focus so Enter/Space on a nested button
      // (watchlist, upgrade CTA) keeps doing its own thing. No role="button":
      // the card contains buttons, and nesting them is an axe violation.
      tabIndex={0}
      aria-current={isSelected ? "true" : undefined}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span className="lrow-accent" aria-hidden="true" />
      {onToggleChecked && (
        <input
          type="checkbox"
          className="lrow-check"
          checked={isChecked}
          aria-label={`Select ${property.address || "this property"}`}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleChecked(property.id)}
        />
      )}

      {/* top — address + score / status / watch cluster */}
      <div className="lrow-top">
        <div className="lrow-address">{displayAddr.text}</div>
        {isAllowedCounty ? (
          <div className="lrow-cluster">
            <DealScore property={property} variant="inline" />
            {completeness.warn && (
              <span className="lrow-pill lrow-pill--muted" title={limitedDataTitle || "Some fields not published by this county"}>
                Limited data
              </span>
            )}
            {vacant && (
              <span className="lrow-pill lrow-pill--muted" title="No structure on file for this parcel — likely a vacant lot">
                Vacant Land
              </span>
            )}
            <span
              className="lrow-pill"
              style={{ color: badge.color, backgroundColor: `${badge.color}14`, borderColor: `${badge.color}40` }}
              title={badge.desc}
            >
              {badge.short}
            </span>
            {badge.foreclosureTypeLabel && (
              <span
                className="lrow-pill lrow-pill--muted"
                style={
                  badge.foreclosureTypeColor
                    ? { color: badge.foreclosureTypeColor, borderColor: `${badge.foreclosureTypeColor}40` }
                    : undefined
                }
                title="Foreclosure type — mortgage and tax foreclosures follow different NC procedures"
              >
                {badge.foreclosureTypeLabel}
              </span>
            )}
            {changeKind && (
              <span className="lrow-pill lrow-pill--changed" title="Changed since your last visit">
                {CHANGE_LABELS[changeKind]}
              </span>
            )}
            {stageLabel && (
              <span className="lrow-pill lrow-pill--stage" title="Deal stage">
                {stageLabel}
              </span>
            )}
            <OtherSignalPills labels={badge.otherSignalLabels} />
            <WatchlistToggle
              variant="icon"
              isWatched={isWatched}
              loading={watchLoading}
              authed={authed}
              proRequired={atLimit}
              onToggle={() => onToggleWatch(property.id)}
              onRequireAuth={onRequireAuth}
              onRequirePro={onRequirePro}
            />
            {authed && onToggleHidden && (
              <button
                type="button"
                className="lrow-hide-btn"
                aria-label={isHidden ? "Unhide this property" : "Not interested: hide this property"}
                title={isHidden ? "Unhide" : "Not interested"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHidden(property.id);
                }}
              >
                {isHidden ? "↺" : "✕"}
              </button>
            )}
          </div>
        ) : (
          <div className="lrow-cluster lrow-cluster--fuzzed" aria-hidden="true">
            <span className="lrow-fuzzed-lock">🔒</span>
          </div>
        )}
      </div>

      {/* meta */}
      <div className="lrow-meta">{metaLine}</div>

      {/* facts — free physical characteristics, shown only when the county publishes them */}
      {factsLine && <div className="lrow-facts">{factsLine}</div>}

      {/* code-violation evidence, compact: "2 open cases · order Mar 4, 2026".
          In-area viewers only (the pill it explains is hidden otherwise). The
          full panel with the source link is in the property modal. */}
      {isAllowedCounty && cvEvidence?.compact && (
        <div className="lrow-cv" data-testid="card-cv-evidence" title={cvEvidence.stageText ?? "Code-enforcement evidence"}>
          {cvEvidence.compact}
        </div>
      )}

      {/* owner of record — present only for entitled (Pro) viewers; PII stays server-gated */}
      {ownerName && (
        <div className="lrow-owner" title={ownerRowTitle}>
          <span className="lrow-owner-label">{ownerRowLabel}</span>
          <span className="lrow-owner-name">{ownerName}</span>
          {property.owner?.isAbsentee && (
            <span className="lrow-tag" title="Owner's mailing address differs from the property — likely absentee">
              Absentee
            </span>
          )}
          {property.owner?.isEntityOwner && (
            <span className="lrow-tag" title="Owned by an LLC, company, or trust">LLC</span>
          )}
        </div>
      )}

      {/* lifecycle (thin, no labels) */}
      <div
        className="lrow-lifecycle"
        title={`Pipeline stage ${lifecycleStage + 1} of 4: ${property.status}`}
        aria-label={`Pipeline stage: ${property.status}`}
      >
        {([0, 1, 2, 3] as const).map((i) => (
          <span key={i} className={"lrow-seg" + (i <= lifecycleStage ? " lrow-seg--filled" : "")} />
        ))}
      </div>

      {/* data strip */}
      {isAllowedCounty ? (
        <div className="lrow-strip">
          <div className="lrow-field">
            <div className="lrow-field-label">Assessed</div>
            <div className="lrow-field-val">{formatCurrencyFull(property.assessedValue)}</div>
          </div>
          <div className="lrow-field">
            <div className="lrow-field-label">{midLabel}</div>
            <div className="lrow-field-val">{formatCurrencyFull(midVal)}</div>
          </div>
          <div className="lrow-field">
            <div className="lrow-field-label">{equityLabel}</div>
            <div className={"lrow-field-val" + (!isTerminal && equityVal && equityVal > 0 ? " lrow-field-val--equity" : "")}>
              {formatCurrencyFull(equityVal)}
            </div>
          </div>
        </div>
      ) : (
        <div className="lrow-strip lrow-strip--fuzzed">
          <div className="lrow-fuzzed-bars" aria-hidden="true">
            <span className="lrow-fuzzed-bar" />
            <span className="lrow-fuzzed-bar" />
            <span className="lrow-fuzzed-bar" />
          </div>
          <div className="lrow-fuzzed-message">
            <span>
              {onPickCounty
                ? "Outside your area — pick your county to see this"
                : "Outside your area — upgrade to see this"}
            </span>
            <button type="button" className="lrow-fuzzed-cta" onClick={handleFuzzedUpgradeClick}>
              {onPickCounty ? "Pick county" : "Upgrade"}
            </button>
          </div>
        </div>
      )}

      {/* footer */}
      <div className="lrow-footer">
        {footerAmber && (
          <svg className="lrow-footer-clock" width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <circle cx="7" cy="7.5" r="5" />
            <path d="M7 4.5v3l2 1.3" />
          </svg>
        )}
        <span className={"lrow-footer-main" + (footerAmber ? " lrow-footer-main--amber" : "")}>{footerMain}</span>
        {footerRight && <span className="lrow-footer-right">{footerRight}</span>}
        {addedDate && (
          <span
            className={"lrow-footer-right" + (footerRight ? " lrow-footer-right--follow" : "")}
            title="Date Parcely first added this property (not the filing date)"
          >
            Added {addedDate}
          </span>
        )}
      </div>
    </div>
  );
};

// Memoized: the list re-renders its visible rows whenever its row context
// changes (selection, watch toggles, bulk checks), and most rows' props are
// unchanged by any one of those.
export default React.memo(PropertyCard);
